<template>
    <v-alert
        ref="alert"
        v-bind="$attrs"
        v-on="$listeners"
        :value="value"
        @input="$emit('input', $event)"
    >
        <slot />
    </v-alert>
</template>

<script>
/**
 * rc-alert - Accessible Alert Component Wrapper
 * 
 * This component wraps Vuetify 2's v-alert component to address accessibility shortcomings. It's
 * meant for use in public-facing interfaces, or any place where accessibility is critical (ideally, everywhere).
 *
 * Problem: Vuetify 2 alerts with dismissible buttons don't automatically focus the close button
 * when they appear, and don't return focus to the triggering element when dismissed, making 
 * them less accessible for keyboard users who rely on focus management.
 * 
 * Solution: This wrapper automatically focuses the close button when the alert becomes visible
 * and returns focus to the previously focused element when dismissed, improving keyboard 
 * navigation and screen reader accessibility while maintaining full compatibility with the 
 * original v-alert API.
 *
 * @example
 * <rc-alert v-model="showAlert" type="error" dismissible>
 *   Error message here
 * </rc-alert>
 */
export default {
    name: 'RcAlert',

    inheritAttrs: false,

    data() {
        return {
            /**
             * Stores reference to the element that had focus before the alert appeared
             * Used to restore focus when the alert is dismissed
             * @type {HTMLElement|null}
             */
            previouslyFocusedElement: null
        }
    },

    props: {
        /**
         * Controls the visibility of the alert
         * @type {Boolean}
         */
        value: {
            type: Boolean,
            default: false
        },

        /**
         * Element to focus when the alert is dismissed.
         * If provided, this takes precedence over automatic focus detection.
         * Useful for mouse interactions where the triggering element doesn't have focus.
         * @type {HTMLElement|String|Object}
         */
        restoreFocusTo: {
            type: [HTMLElement, String, Object],
            default: null
        }
    },

    watch: {
        value: {
            immediate: true,
            /**
             * Watches for alert visibility changes and manages focus accordingly
             * @param {Boolean} newValue - New visibility state
             * @param {Boolean} oldValue - Previous visibility state
             */
            handler(newValue, oldValue) {
                if (newValue && !oldValue) {
                    if (this.restoreFocusTo) {
                        if (typeof this.restoreFocusTo === 'string') {
                            this.previouslyFocusedElement = document.querySelector(this.restoreFocusTo);
                        } else if (this.restoreFocusTo.$el) {
                            this.previouslyFocusedElement = this.restoreFocusTo.$el;
                        } else {
                            this.previouslyFocusedElement = this.restoreFocusTo;
                        }
                    } else {
                        // fall back to current active element (handles keyboard navigation)
                        this.previouslyFocusedElement = document.activeElement;
                    }

                    this.$nextTick(() => {
                        this.focusCloseButton();
                    });
                } else if (!newValue && oldValue) {
                    this.$nextTick(() => {
                        this.restorePreviousFocus();
                    });
                }
            }
        }
    },

    methods: {
        /**
         * Restores focus to the element that was focused before the alert appeared.
         * This completes the accessibility focus management cycle.
         */
        restorePreviousFocus() {
            if (this.previouslyFocusedElement &&
                typeof this.previouslyFocusedElement.focus === 'function' &&
                document.contains(this.previouslyFocusedElement)) {
                this.previouslyFocusedElement.focus();
                this.previouslyFocusedElement = null;
            }
        },

        /**
         * Focuses the alert's close button for improved keyboard accessibility.
         * 
         * The method safely handles cases where:
         * - The alert ref doesn't exist
         * - The alert DOM element isn't available
         * - The close button isn't present (non-dismissible alerts)
         */
        focusCloseButton() {
            if (!this.$refs.alert?.$el) {
                return;
            }

            const closeButton = this.$refs.alert.$el.querySelector('.v-alert__dismissible');

            if (closeButton) {
                closeButton.focus();
            }
        }
    }
}
</script>

<style lang="scss" scoped></style>